import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  InputGroup,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiCheck, FiPlus, FiTrash } from 'react-icons/all';
import { FiChevronLeft, FiEdit2, FiX } from 'react-icons/fi';
import { Input, Table } from '~/components';
import { useDocumentsTypes } from '~/hooks/useDocuments/useDocuments';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import { SelectItem } from '~/types';
import { useNotExtendingReasons } from '~/hooks/useExtensions/useExtensions';

type FormData = {
  motivo: string;
};

interface ReasonFormProps {
  align: boolean;
}

export const ReasonForm: React.FC<ReasonFormProps> = ({
  align,
}: ReasonFormProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef(null);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isEdit, setEdit] = useState<boolean[]>([]);
  const [motivo, setMotivo] = useState<string>('');
  const [motivoEdit, setMotivoEdit] = useState<SelectItem[]>([]);
  const [isFetching, setFetching] = useState(false);
  const toast = useToast();
  const {
    data: motivos,
    isLoading: isLoadingMotivos,
    refetch,
  } = useNotExtendingReasons();

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    clearErrors();
    onClose();
  };

  async function handleCreate(formData: FormData) {
    clearErrors();

    (!motivo || !motivo?.trim().length) &&
      setError('motivo', {
        type: 'required',
        message: 'Campo obrigatório',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = { motivo: motivo?.trim() };

    setFetching(true);

    api
      .post(`proposta_extensao/motivos`, form)
      .then(() => {
        toast({
          title: `Motivo adicionado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setMotivo('');
        setValue('motivo', '');
        queryClient.invalidateQueries('not-extending-reasons');
      })
      .catch(error => {
        if (error.response.status === 400) {
          const errorsValidate = Object.entries(error.response.data);
          errorsValidate.forEach(err => {
            toast({
              title: `Erro ao criar motivo`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao adicionar motivo`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  async function handleSubmitEdit(index: number) {
    clearErrors();

    (!motivoEdit[index] || !motivoEdit[index].label.trim().length) &&
      setError(`type-${index}`, {
        type: 'required',
        message: 'Escreva o motivo',
      });

    motivoEdit[index].label.length > 60 &&
      setError(`type-${index}`, {
        type: 'maxLength',
        message: 'Motivo com mais de 60 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = { motivo: motivoEdit[index].label.trim() };

    setFetching(true);

    api
      .put(`proposta_extensao/${motivoEdit[index]?.value}/motivos-id`, form)
      .then(() => {
        toast({
          title: `Motivo editado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setMotivoEdit([...motivoEdit]);
        handleEdit(index);
        queryClient.invalidateQueries('not-extending-reasons');
      })
      .catch(error => {
        toast({
          title: `Erro ao editar motivo`,
          description: `${
            motivoEdit[index].label.length > 60
              ? 'Motivo com mais de 60 caracteres'
              : !motivoEdit[index].label.trim().length
              ? 'Motivo não pode ser vazio'
              : 'Motivo não pode ser repetido'
          }`,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setFetching(false);
      });
  }

  const handleEdit = (i: number, value?: SelectItem) => {
    clearErrors();
    isEdit[i] = !isEdit[i];
    setEdit([...isEdit]);
    if (value) {
      motivoEdit[i] = value;
      setMotivoEdit([...motivoEdit]);
    }
  };

  const canEdit = (index: number) => {
    return isEdit[index];
  };

  const handleMotivo = (event: any) => {
    setMotivo(event.target.value);
    clearErrors();
  };

  const handleMotivoEdit = (event: any, index: number, id: number) => {
    motivoEdit[index] = { value: id.toString(), label: event.target.value };
    clearErrors(`type-${index}`);
  };
  async function handleDelete(id: number) {
    api
      .delete(`proposta_extensao/${id}/motivos-id`)
      .then(() => {
        toast({
          title: `Motivo deletado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('not-extending-reasons');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: `${error.response.data.detail}`,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  return (
    <>
      <Box
        as="button"
        ref={btnRef}
        onClick={handleOpen}
        transition="all 0.2s cubic-bezier(.08,.52,.52,1)"
        border="1px"
        px="8px"
        borderRadius="4px"
        fontSize="sm"
        bg="gray.900"
        borderColor="gray.900"
        color="#fff"
        ml={4}
        h="40px"
        alignSelf={align ? 'center' : 'flex-end'}
      >
        <Flex align="center" gridGap="2">
          <Text fontSize={{ lg: 'sm', md: 'sm' }}>Novo</Text>
        </Flex>
      </Box>
      <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="lg">
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton />
          <DrawerHeader>
            <Icon
              as={FiChevronLeft}
              style={{
                cursor: 'pointer',
                fontSize: '28px',
                marginRight: '10px',
              }}
              onClick={handleClose}
            />
            Cadastro de motivo
          </DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" w={600}>
              <Stack gridGap={4} />
              <FormControl>
                <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                  Novo motivo
                </FormLabel>
                <InputGroup>
                  <FormControl isInvalid={errors?.motivo}>
                    <Input
                      name="motivo"
                      type="text"
                      error={errors.motivo}
                      fontSize="15px"
                      placeholder="Entre com o novo motivo"
                      onChange={handleMotivo}
                      value={motivo}
                      maxLength={50}
                    />
                    <small>
                      <p style={{ color: 'gray' }}>
                        &nbsp;&nbsp;Caracteres restantes: {50 - motivo.length}
                      </p>
                    </small>
                  </FormControl>
                  <Button
                    type="submit"
                    color="white"
                    background="gray.900"
                    _hover={{
                      bgColor: 'black',
                    }}
                    style={{ marginLeft: '10px' }}
                    onClick={handleSubmit(handleCreate)}
                  >
                    <Icon as={FiPlus} />
                  </Button>
                </InputGroup>
              </FormControl>
              <Table type="secondary">
                <Thead>
                  <Tr>
                    <Th>Motivos cadastrados</Th>
                    <Th style={{ textAlign: 'right', paddingRight: '45px' }}>
                      Ações
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {motivos &&
                    !isLoadingMotivos &&
                    motivos?.map((t: any, index: number) => (
                      <Tr key={t.value}>
                        {!canEdit(index) ? (
                          <>
                            <Td>
                              <Tooltip label={t.label} placement="top">
                                <Text isTruncated style={{ width: '240px' }}>
                                  {t.label}
                                </Text>
                              </Tooltip>
                            </Td>
                            <Td
                              style={{
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Tooltip label="Editar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Edit type"
                                  icon={<Icon as={FiEdit2} />}
                                  onClick={() => handleEdit(index, t)}
                                  marginRight="5px"
                                />
                              </Tooltip>
                              <Tooltip label="Deletar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Remove type"
                                  icon={<Icon as={FiTrash} />}
                                  onClick={() => handleDelete(t.value)}
                                />
                              </Tooltip>
                            </Td>
                          </>
                        ) : (
                          <>
                            <Td>
                              <FormControl isInvalid={errors[`type-${index}`]}>
                                <Input
                                  name="type"
                                  type="text"
                                  error={errors[`type-${index}`]}
                                  fontSize="15px"
                                  placeholder="Editar motivo"
                                  size="sm"
                                  defaultValue={motivoEdit[index].label}
                                  onChange={e =>
                                    handleMotivoEdit(e, index, t.value)
                                  }
                                />
                              </FormControl>
                            </Td>
                            <Td style={{ textAlign: 'right' }}>
                              <Tooltip label="Confirmar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Confirm"
                                  icon={<Icon as={FiCheck} />}
                                  marginRight="5px"
                                  onClick={() => handleSubmitEdit(index)}
                                />
                              </Tooltip>
                              <Tooltip label="Cancelar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Cancel"
                                  icon={<Icon as={FiX} />}
                                  onClick={() => handleEdit(index)}
                                />
                              </Tooltip>
                            </Td>
                          </>
                        )}
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </Stack>
            <Button
              type="submit"
              onClick={handleClose}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px', marginBottom: '20px' }}
            >
              Voltar
            </Button>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
